import React, { useState, useEffect } from 'react';
import { getCategoriasFlat, getFasesDeObra, addItem, updateItem, getItens } from '../services/api';
import { getHierarchicalCategories } from '../utils/categoryUtils';
import { Categoria, FaseObra, Item } from '../services/types';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    InputAdornment,
    Alert,
    Autocomplete,
    Chip
} from '@mui/material';

interface ItemEditModalProps {
    item: Partial<Item> | null;
    open: boolean;
    onClose: () => void;
}

const ItemEditModal: React.FC<ItemEditModalProps> = ({ item, open, onClose }) => {
    const [formData, setFormData] = useState<Partial<Item>>({ fases: [] });
    const [categorias, setCategorias] = useState<Categoria[]>([]);
    const [fasesObra, setFasesObra] = useState<FaseObra[]>([]);
    const [existingItems, setExistingItems] = useState<any[]>([]);
    const [error, setError] = useState<string>('');
    const [saving, setSaving] = useState<boolean>(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [cats, fases, itens] = await Promise.all([
                    getCategoriasFlat(),
                    getFasesDeObra(),
                    getItens()
                ]);
                setCategorias(cats || []);
                setFasesObra(fases || []);
                setExistingItems(itens || []);
            } catch (err) {
                console.error("Failed to fetch data for modal", err);
                setError('Não foi possível carregar os dados necessários para o formulário.');
            }
        };

        if (open) {
            fetchData();
        }
    }, [open]);

    useEffect(() => {
        if (item) {
            setFormData({
                ...item,
                valor_unitario_padrao: item.valor_unitario_padrao ?? 0,
                fases: item.fases || []
            });
        } else {
            setFormData({ descricao: '', valor_unitario_padrao: 0, categoria_id: undefined, fases: [] });
        }
        setError('');
    }, [item, open]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSave = async () => {
        setError('');
        const descricaoTrimmed = (formData.descricao || '').trim();
        const valorNum = formData.valor_unitario_padrao === undefined || formData.valor_unitario_padrao === null || String(formData.valor_unitario_padrao) === ''
            ? NaN
            : Number(formData.valor_unitario_padrao);

        if (!descricaoTrimmed || !formData.categoria_id || isNaN(valorNum) || valorNum < 0) {
            setError('Preencha a Descrição, selecione uma Subcategoria e informe um Valor Unitário válido (maior ou igual a R$ 0,00).');
            return;
        }

        const faseIds = formData.fases?.map(f => f.id) || [];
        if (faseIds.length === 0) {
            setError('Selecione pelo menos uma Fase da Obra para que o item possa ser utilizado nos lançamentos.');
            return;
        }

        const payload = {
            descricao: descricaoTrimmed,
            valor_unitario_padrao: valorNum,
            categoria_id: formData.categoria_id,
        };

        setSaving(true);
        try {
            if (formData.id) {
                await updateItem(formData.id, payload, faseIds);
            } else {
                await addItem(payload, faseIds);
            }
            onClose();
        } catch (err: any) {
            console.error("Failed to save item", err);
            setError(err.message || 'Falha ao salvar o item.');
        } finally {
            setSaving(false);
        }
    };

    const hierarchicalCats = getHierarchicalCategories(categorias);
    const categoriaValue = hierarchicalCats.find(c => c.id === formData.categoria_id) || null;
    const fasesValue = formData.fases?.map(fase => fasesObra.find(f => f.id === fase.id)).filter(Boolean) as FaseObra[] || [];

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{formData.id ? 'Editar Item' : 'Adicionar Novo Item'}</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: '16px !important' }}>
                {error && <Alert severity="error">{error}</Alert>}
                <Autocomplete
                    freeSolo
                    options={existingItems.map((option) => option.descricao)}
                    renderOption={(props, option) => (
                        <li {...props}>
                            {option} <span style={{ color: 'gray', fontSize: '0.8em', marginLeft: '8px' }}>(Cadastrado)</span>
                        </li>
                    )}
                    value={formData.descricao || ''}
                    onInputChange={(event, newInputValue) => {
                        setFormData(prev => ({ ...prev, descricao: newInputValue }));
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            name="descricao"
                            label="Descrição do Item"
                            required
                            fullWidth
                        />
                    )}
                />
                <Autocomplete
                    options={hierarchicalCats}
                    getOptionLabel={(option) => option.parentName ? `${option.parentName} > ${option.nome}` : option.nome}
                    value={categoriaValue}
                    onChange={(event, newValue) => {
                        setFormData(prev => ({ ...prev, categoria_id: newValue ? newValue.id : undefined }));
                    }}
                    renderOption={(props, option) => {
                        return (
                            <li
                                {...props}
                                style={{
                                    paddingLeft: `${(option.level * 20) + 12}px`,
                                    fontWeight: option.level === 0 ? 700 : 400
                                }}
                            >
                                {option.level > 0 ? '— '.repeat(option.level) : ''}{option.nome}
                            </li>
                        );
                    }}
                    getOptionDisabled={(option) => option.isParent}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Subcategoria"
                            required
                            helperText="Selecione uma subcategoria dentro da categoria principal"
                        />
                    )}
                    fullWidth
                />
                <Autocomplete
                    multiple
                    id="fases-obra-autocomplete"
                    options={fasesObra}
                    groupBy={(option) => option.tipo_fluxo === 'leilao' ? '🏠 Leilão' : '🏗️ Construção'}
                    getOptionLabel={(option) => option.nome}
                    value={fasesValue}
                    onChange={(event, newValue) => {
                        setFormData(prev => ({ ...prev, fases: newValue }));
                    }}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => {
                            const { key, ...tagProps } = getTagProps({ index });
                            return <Chip key={key} variant="outlined" label={option.nome} {...tagProps} />;
                        })
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="outlined"
                            label="Fases da Obra Associadas *"
                            placeholder="Selecione as fases"
                        />
                    )}
                    fullWidth
                />
                <TextField
                    name="valor_unitario_padrao"
                    label="Valor Unitário Padrão"
                    type="number"
                    value={formData.valor_unitario_padrao ?? 0}
                    onChange={handleChange}
                    required
                    fullWidth
                    inputProps={{ min: 0, step: "0.01" }}
                    helperText="Use 0 para serviços ou itens com valor variável"
                    InputProps={{
                        startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                    }}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>Cancelar</Button>
                <Button onClick={handleSave} variant="contained" disabled={saving}>
                    {saving ? 'Salvando...' : 'Salvar'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ItemEditModal;
